import { useState, useEffect } from 'react'
import styled from '@emotion/styled'
import { MediaQueries as Mq } from '../../styles/settings/theme'
import { useChartDimensions } from '../../hooks/dimensions'
import { csv } from 'd3-fetch'
import { Flex } from '../atoms/Basics'
import Card from '../molecules/Card'
import Note from '../atoms/Note'
import YearSelector from '../atoms/yearSelector'
import expand from '../../assets/svgs/expand.svg'

const CardContainer = styled.div`
  max-width: ${(props) => props.theme.widths[2]};
  margin: 0 auto;
  //padding: 4rem 0 0;
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  column-gap: 1rem;
  row-gap: 1rem;
  ${Mq.lg} {
    grid-template-columns: 1fr 1fr;
  }
  ${Mq.sm} {
    grid-template-columns: 1fr;
  }
`

const StyledDiv = styled.div`
  max-width: ${(props) => props.theme.widths[1]};
  margin: 0 auto;
  padding: 0 0 2rem;
`
const Expand = styled(Flex)`
  max-width: ${(props) => props.theme.widths[2]};
  margin: 0 auto;
  padding: 4rem 0 1rem;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
`

const ExpandHint = styled(Flex)`
  align-items: center;
  justify-content: center;
  gap: 1rem;

  p {
    color: ${(props) => props.theme.colors.orange};
    font-weight: 700;
  }
`

const Cards = ({ note }) => {
  const [allStrengthData, setAllStrengthData] = useState(null)
  const [allWeaknessData, setAllWeaknessData] = useState(null)
  const [allTechUseData, setAllTechUseData] = useState(null)
  const [allNeedsData, setAllNeedsData] = useState(null)
  const [strengthData, setStrengthData] = useState(null)
  const [weaknessData, setWeaknessData] = useState(null)
  const [techUseData, setTechUseData] = useState(null)
  const [needsData, setNeedsData] = useState(null)
  const [availableYears, setAvailableYears] = useState([])
  const [selectedYear, setSelectedYear] = useState('All Years')
  const [maxVal, setMaxVal] = useState()

  useEffect(() => {
    Promise.all([
      csv(process.env.PUBLIC_URL + '/data/strengths.csv'),
      csv(process.env.PUBLIC_URL + '/data/weaknesses.csv'),
      csv(process.env.PUBLIC_URL + '/data/techUse.csv'),
      csv(process.env.PUBLIC_URL + '/data/needs.csv')
    ]).then((data) => {
      setAllStrengthData(data[0])
      setAllWeaknessData(data[1])
      setAllTechUseData(data[2])
      setAllNeedsData(data[3])
    })
  }, [])

  useEffect(() => {
    if (allStrengthData && allWeaknessData && allTechUseData && allNeedsData) {
      // Extract unique years from all datasets
      const strengthYears = allStrengthData.map((row) => row.year)
      const weaknessYears = allWeaknessData.map((row) => row.year)
      const techUseYears = allTechUseData.map((row) => row.year)
      const needsYears = allNeedsData.map((row) => row.year)
      const allYears = [
        ...new Set([
          ...strengthYears,
          ...weaknessYears,
          ...techUseYears,
          ...needsYears
        ])
      ].sort((a, b) => {
        if (a === 'All Years') return -1
        if (b === 'All Years') return 1
        return a.localeCompare(b)
      })
      setAvailableYears(allYears)
    }
  }, [allStrengthData, allWeaknessData, allTechUseData, allNeedsData])

  useEffect(() => {
    if (
      allStrengthData &&
      allWeaknessData &&
      allTechUseData &&
      allNeedsData &&
      selectedYear
    ) {
      // Filter data by selected year
      const filteredStrengthData = allStrengthData.filter(
        (row) => row.year === selectedYear
      )
      const filteredWeaknessData = allWeaknessData.filter(
        (row) => row.year === selectedYear
      )
      const filteredTechUseData = allTechUseData.filter(
        (row) => row.year === selectedYear
      )
      const filteredNeedssData = allNeedsData.filter(
        (row) => row.year === selectedYear
      )

      // Get valid columns (excluding those with all NA values for the selected year)
      const getValidColumns = (data, originalColumns) => {
        if (!data || data.length === 0 || !originalColumns) return []

        const validColumns = originalColumns.filter((col, index) => {
          // Always keep year and category columns (first two)
          if (index < 2) return true

          // Check if this column has any non-NA values in the filtered data
          const hasValidData = data.some((row) => {
            const value = row[col]
            return (
              value !== 'NA' &&
              value !== '' &&
              value !== undefined &&
              value !== null
            )
          })
          return hasValidData
        })

        return validColumns
      }

      // Get valid columns for each dataset
      const strengthValidColumns = getValidColumns(
        filteredStrengthData,
        allStrengthData.columns
      )
      const weaknessValidColumns = getValidColumns(
        filteredWeaknessData,
        allWeaknessData.columns
      )
      const needsValidColumns = getValidColumns(
        filteredNeedssData,
        allNeedsData.columns
      )
      const techUseValidColumns = allTechUseData.columns // TechUse doesn't have NA issues

      // Preserve the valid columns property
      if (filteredStrengthData.length > 0) {
        filteredStrengthData.columns = strengthValidColumns
      }
      if (filteredWeaknessData.length > 0) {
        filteredWeaknessData.columns = weaknessValidColumns
      }
      if (filteredNeedssData.length > 0) {
        filteredNeedssData.columns = needsValidColumns
      }
      if (filteredTechUseData.length > 0) {
        filteredTechUseData.columns = techUseValidColumns
      }

      setStrengthData(filteredStrengthData)
      setWeaknessData(filteredWeaknessData)
      setNeedsData(filteredNeedssData)
      setTechUseData(filteredTechUseData)

      // Calculate max value for scaling - only if we have valid data
      if (
        filteredStrengthData.length > 0 &&
        filteredWeaknessData.length > 0 &&
        filteredNeedssData.length > 0
      ) {
        const strengthMax = findHighestVal(filteredStrengthData)
        const weaknessMax = findHighestVal(filteredWeaknessData)
        const needsMax = findHighestVal(filteredNeedssData)
        if (strengthMax > 0 || weaknessMax > 0 || needsMax > 0) {
          setMaxVal(Math.max(strengthMax, weaknessMax, needsMax))
        }
      }
    }
  }, [
    allStrengthData,
    allWeaknessData,
    allTechUseData,
    allNeedsData,
    selectedYear
  ])

  const findHighestVal = (data) => {
    if (!data || !data.columns) return 0
    const cols = data.columns
    const highVals = cols
      .map((col) => {
        return Math.max.apply(
          Math,
          data.map((d) => {
            const val = d[col]
            // Skip NA, empty strings, and non-numeric values
            if (val === 'NA' || val === '' || val === undefined || val === null)
              return 0
            return parseFloat(val) || 0
          })
        )
      })
      .slice(2) // Skip year and first data column (Strengths/Weaknesses/Needs)
    return Math.max(...highVals)
  }

  const handleYearChange = (event) => {
    setSelectedYear(event.target.value)
  }

  //get column names - same for each data set, skip year column
  const cardTitles =
    strengthData && strengthData.columns ? strengthData.columns.slice(2) : null

  //divides data by card topic --> obj with strength/weakness name and value
  const groupData = (type, title) => {
    let obj = {}
    const data =
      type === 'Strengths'
        ? strengthData
        : type === 'Weaknesses'
        ? weaknessData
        : type === 'Needs'
        ? needsData
        : null
    if (data) {
      const columnName = type === 'Needs' ? 'Needs' : type
      data?.map((d) => (obj[d[columnName]] = d[title]))
    }
    return obj
  }

  // Component with data loaded check
  return (
    <LoadedCards
      strengthData={strengthData}
      weaknessData={weaknessData}
      techUseData={techUseData}
      needsData={needsData}
      availableYears={availableYears}
      selectedYear={selectedYear}
      handleYearChange={handleYearChange}
      cardTitles={cardTitles}
      groupData={groupData}
      maxVal={maxVal}
      note={note}
    />
  )
}

// Separate component that uses the hook only when data is loaded
const LoadedCards = ({
  techUseData,
  availableYears,
  selectedYear,
  handleYearChange,
  cardTitles,
  groupData,
  maxVal,
  note
}) => {
  const [selectedRow, setSelectedRow] = useState(1)

  const [ref, dimensions] = useChartDimensions({
    marginTop: 0,
    marginRight: 0,
    marginBottom: 0,
    marginLeft: 0
  })
  const handleSelectedRow = (i) => {
    const row = getRow(i)
    if (row === selectedRow) setSelectedRow(null)
    else setSelectedRow(row)
  }

  const getRow = (i) => {
    if (!dimensions) return 1
    //i.e. mq dimension (width at which grid changes) - 2rem --> 740 - 32 = 708
    if (dimensions.width < 708) {
      return i + 1
    }
    //1080 - 32 = 1048
    if (dimensions.width < 1048) {
      return i < 2
        ? 1
        : i < 4
        ? 2
        : i < 6
        ? 3
        : i < 8
        ? 4
        : i < 10
        ? 5
        : i < 12
        ? 6
        : i < 14
        ? 7
        : i < 16
        ? 8
        : i < 18
        ? 9
        : i < 20
        ? 10
        : 11
    }
    return i < 3
      ? 1
      : i < 6
      ? 2
      : i < 9
      ? 3
      : i < 12
      ? 4
      : i < 15
      ? 5
      : i < 18
      ? 6
      : i < 21
      ? 7
      : 8
  }

  return (
    <>
      <Expand>
        <ExpandHint>
          <img alt="expand" src={expand} width="24" />
          <p>click on a technology to expand and learn more</p>
        </ExpandHint>
        <YearSelector value={selectedYear} onChange={handleYearChange}>
          {availableYears.map((year) => (
            <option key={year} value={year}>
              {year}
            </option>
          ))}
        </YearSelector>
      </Expand>
      <CardContainer ref={ref}>
        {cardTitles?.map((title, i) => (
          <Card
            key={i}
            title={title}
            sData={groupData('Strengths', title)}
            wData={groupData('Weaknesses', title)}
            eData={groupData('Needs', title)}
            pieData={techUseData?.find((d) => d.group === title)}
            i={i}
            handleSelectedRow={handleSelectedRow}
            selectedRow={selectedRow}
            row={getRow(i)}
            scaleTo={maxVal}
          />
        ))}
      </CardContainer>
      <StyledDiv>
        <Note note={note} />
      </StyledDiv>
    </>
  )
}

export default Cards
